import React, {useMemo, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {removeTerminalVideo} from '@/entities/stream/api';
import {fetchControlDepartments} from '@/entities/attendance/api';
import {
  bulkAssignProfile,
  bulkCommand,
  bulkForceSync,
  bulkSetDeviceDepartment,
  bulkSetGateMode,
  fetchDevices,
  fetchProfiles,
  finalizeDeviceReset,
  type BulkCommandKind,
  type SyncMode,
} from '@/entities/device/api';
import {
  isTerminalLike,
  type DeviceSummary,
  type GateMode,
} from '@/entities/device/types';
import {ApiError} from '@/shared/api';
import {runBulk, type BulkOutcome} from '@/shared/api/bulk';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {ChoiceChips, Segmented} from '@/shared/ui/segmented';
import {SectionHeader} from '@/shared/ui/section';
import {Sheet} from '@/shared/ui/sheet';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatusDot} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Everything that acts on MANY terminals at once.
 *
 * The gateway has offered six `/control/devices/bulk/*` routes for as long as the
 * single-device ones, and the cabinet called none of them: on a site with forty doors
 * the difference is half an hour of tapping versus one action. This is that action,
 * built as the twin of `PersonnelToolsSheet` — same 500-id chunking, same honest
 * reporting of partial success, same reason for being a sheet rather than a mode on
 * the list.
 *
 * ## Two routes are deliberately not wired to their own button
 *
 * **`POST /control/devices/bulk/delete`** leaves every licence seat occupied, exactly
 * as its single-device twin does. So «удалить» here loops `finalizeDeviceReset`
 * instead — one call per terminal, slower on the wire, and the only version that
 * gives the seats back. It is idempotent, so a half-finished sweep is healed by
 * running it again, and it works on boards that are lost or dead. The wording says
 * what it does and does not do: the cloud forgets the terminal, the terminal keeps
 * its faces until somebody wipes it.
 *
 * **`factory_reset` is not in the command list.** The batch command route whitelists
 * five kinds and excludes that one on purpose — precisely because it would wipe boards
 * without freeing their seats. The single-device route validates nothing and would
 * take it; this screen does not offer the hole.
 *
 * ## What the picker shows
 *
 * Only terminal-like rows. A camera has no door to open, no gate mode and no personnel
 * to sync, and a desktop bridge is not standing at one — offering them here would be
 * offering five actions that answer `480` for reasons the operator cannot see.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  block: {gap: spacing(2)},
  card: {gap: spacing(3)},
  row: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  grow: {flexGrow: 1, flexBasis: 150},
  list: {maxHeight: 260},
  device: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingVertical: spacing(2),
    paddingHorizontal: spacing(2),
    borderRadius: radii.sm,
  },
  deviceText: {flex: 1, gap: 1},
  box: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tick: {width: 10, height: 10, borderRadius: 2},
});

type BulkAction =
  | 'department'
  | 'profile'
  | 'gateMode'
  | 'sync'
  | 'command'
  | 'remove';

/** How many terminals the sheet loads to choose from. */
const FLEET_LIMIT = 500;

/** The five the batch route accepts. `factory_reset` is excluded — see the module note. */
const COMMANDS: readonly BulkCommandKind[] = [
  'open_door',
  'pulse_relay',
  'set_led',
  'switch_camera',
  'ota_update',
];

export function DeviceToolsSheet({
  companyId,
  visible,
  onClose,
}: {
  companyId: string;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState<BulkAction>('sync');
  const [departmentId, setDepartmentId] = useState('');
  const [profileId, setProfileId] = useState('');
  const [gateMode, setGateMode] = useState<GateMode>('both');
  const [syncMode, setSyncMode] = useState<SyncMode>('full');
  const [command, setCommand] = useState<BulkCommandKind>('open_door');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);

  const fleet = useQuery({
    queryKey: ['devices', companyId, 'bulk'],
    queryFn: () => fetchDevices(companyId, {perPage: FLEET_LIMIT, page: 1}),
    enabled: visible,
  });

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId),
    enabled: visible,
  });

  const profiles = useQuery({
    queryKey: ['profiles', companyId],
    queryFn: () => fetchProfiles(companyId),
    enabled: visible,
  });

  const filtered = useMemo(() => {
    // Read through `fleet.data` INSIDE the memo: a `?? []` fallback in the render body
    // is a new identity every render and would defeat the memo entirely.
    const source: DeviceSummary[] = fleet.data?.data ?? [];
    const terminals = source.filter(d => isTerminalLike(d.device_type));
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) {
      return terminals;
    }
    return terminals.filter(d =>
      [d.device_name, d.location_label, d.model]
        .filter(Boolean)
        .some(field => String(field).toLocaleLowerCase().includes(needle)),
    );
  }, [fleet.data, search]);

  const toggle = (deviceId: string) =>
    setSelected(current =>
      current.includes(deviceId)
        ? current.filter(id => id !== deviceId)
        : [...current, deviceId],
    );

  const apply = useMutation({
    mutationFn: async (): Promise<BulkOutcome> => {
      switch (action) {
        case 'department':
          // '' is a deliberate CLEAR here, not a missing value — which is why the
          // picker offers «без отдела» as a choice rather than leaving it unset.
          return runBulk(selected, batch =>
            bulkSetDeviceDepartment(companyId, batch, departmentId),
          );
        case 'profile':
          return runBulk(selected, batch =>
            bulkAssignProfile(companyId, batch, profileId),
          );
        case 'gateMode':
          return runBulk(selected, batch =>
            bulkSetGateMode(companyId, batch, gateMode),
          );
        case 'sync':
          return runBulk(selected, batch =>
            bulkForceSync(companyId, batch, syncMode),
          );
        case 'command':
          return runBulk(selected, batch => bulkCommand(companyId, batch, command));
        case 'remove':
          // NOT bulk/delete — see the module note. One finalize per terminal, because
          // that is the only shape that gives the licence seats back.
          return removeEach(companyId, selected).then(async result => {
            // Видео терминалов — отдельные источники; убрать и их (см. removeTerminalVideo).
            const rows = fleet.data?.data ?? [];
            await Promise.all(
              rows
                .filter(d => selected.includes(d.device_id))
                .map(d => removeTerminalVideo(companyId, d)),
            );
            return result;
          });
      }
    },
    onSuccess: async result => {
      setOutcome(result);
      setSelected([]);
      setConfirmRemove(false);
      await qc.invalidateQueries({queryKey: ['devices', companyId]});
      await qc.invalidateQueries({queryKey: ['stream-cameras', companyId]});
    },
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  const departmentOptions = [
    {value: '', label: t('deviceAdmin.noDepartment')},
    ...(departments.data?.departments ?? []).map(d => ({
      value: d.department_id,
      label: d.name,
    })),
  ];

  const profileOptions = [
    {value: '', label: t('deviceBulk.noProfile')},
    ...(profiles.data ?? []).map(pr => ({value: pr.profile_id, label: pr.name})),
  ];

  const canApply =
    selected.length > 0 &&
    !apply.isPending &&
    (action !== 'remove' || confirmRemove) &&
    (action !== 'department' || departments.data !== undefined) &&
    (action !== 'profile' || profiles.data !== undefined);

  return (
    <Sheet
      visible={visible}
      onClose={() => {
        setOutcome(null);
        setConfirmRemove(false);
        apply.reset();
        onClose();
      }}
      title={t('deviceBulk.title')}
      subtitle={t('deviceBulk.subtitle')}
      testID="device-tools">
      <View style={styles.root}>
        <View style={styles.block}>
          <SectionHeader
            title={t('deviceBulk.select')}
            hint={t('deviceBulk.selected', {count: selected.length})}
          />
          <AppInput
            label=""
            icon="search"
            value={search}
            onChangeText={setSearch}
            placeholder={t('home.devices.searchPlaceholder')}
            autoCapitalize="none"
            testID="device-bulk-search"
          />
          {fleet.isPending ? (
            <Skeleton height={180} radius={radii.lg} />
          ) : filtered.length === 0 ? (
            <Card padding={4}>
              <AppText variant="caption">{t('deviceBulk.noTerminals')}</AppText>
            </Card>
          ) : (
            <>
              <Card padding={2}>
                <ScrollView style={styles.list} nestedScrollEnabled>
                  {filtered.map(device => {
                    const on = selected.includes(device.device_id);
                    return (
                      <Pressable
                        key={device.device_id}
                        accessibilityRole="checkbox"
                        accessibilityState={{checked: on}}
                        onPress={() => toggle(device.device_id)}
                        style={({pressed}) => [
                          styles.device,
                          {
                            backgroundColor: on
                              ? p.accent
                              : pressed
                              ? p.muted
                              : 'transparent',
                          },
                        ]}
                        testID={`device-bulk-${device.device_id}`}>
                        <View
                          style={[
                            styles.box,
                            {
                              borderColor: on ? p.primary : p.input,
                              backgroundColor: on ? p.primary : 'transparent',
                            },
                          ]}>
                          {on ? (
                            <View
                              style={[
                                styles.tick,
                                {backgroundColor: p.primaryForeground},
                              ]}
                            />
                          ) : null}
                        </View>
                        <View style={styles.deviceText}>
                          <AppText variant="body" numberOfLines={1}>
                            {device.device_name}
                          </AppText>
                          <AppText variant="caption" numberOfLines={1}>
                            {device.location_label || device.model}
                          </AppText>
                        </View>
                        {/* Offline is grey, never red: a terminal that is not
                            answering is not an alarm — but it IS the reason a
                            queued command sits until it comes back. */}
                        <StatusDot tone={device.online ? 'online' : 'neutral'} />
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </Card>
              <View style={styles.row}>
                <AppButton
                  title={t('bulk.selectAll')}
                  tone="ghost"
                  size="sm"
                  onPress={() => setSelected(filtered.map(d => d.device_id))}
                  style={styles.grow}
                  testID="device-bulk-all"
                />
                <AppButton
                  title={t('bulk.clearSelection')}
                  tone="ghost"
                  size="sm"
                  disabled={selected.length === 0}
                  onPress={() => setSelected([])}
                  style={styles.grow}
                />
              </View>
            </>
          )}
        </View>

        <View style={styles.block}>
          <SectionHeader title={t('deviceBulk.action')} />
          <Card padding={4} style={styles.card}>
            <ChoiceChips
              value={action}
              onChange={next => {
                setAction(next as BulkAction);
                setConfirmRemove(false);
                setOutcome(null);
              }}
              testID="device-bulk-action"
              options={[
                {value: 'sync', label: t('deviceBulk.actionSync')},
                {value: 'department', label: t('deviceBulk.actionDepartment')},
                {value: 'profile', label: t('deviceBulk.actionProfile')},
                {value: 'gateMode', label: t('deviceBulk.actionGateMode')},
                {value: 'command', label: t('deviceBulk.actionCommand')},
                {value: 'remove', label: t('deviceBulk.actionRemove')},
              ]}
            />

            {action === 'sync' ? (
              <>
                <AppText variant="caption">{t('deviceAdmin.syncHint')}</AppText>
                <Segmented
                  value={syncMode}
                  onChange={setSyncMode}
                  testID="device-bulk-sync-mode"
                  options={[
                    {value: 'full', label: t('deviceAdmin.syncFull')},
                    {value: 'settings', label: t('deviceAdmin.syncSettings')},
                    {value: 'personnel', label: t('deviceAdmin.syncPersonnel')},
                  ]}
                />
              </>
            ) : null}

            {action === 'department' ? (
              <>
                <AppText variant="caption">
                  {t('deviceAdmin.departmentHint')}
                </AppText>
                <ChoiceChips
                  value={departmentId}
                  onChange={setDepartmentId}
                  options={departmentOptions}
                  testID="device-bulk-department"
                />
              </>
            ) : null}

            {action === 'profile' ? (
              <>
                <AppText variant="caption">{t('deviceBulk.profileHint')}</AppText>
                <ChoiceChips
                  value={profileId}
                  onChange={setProfileId}
                  options={profileOptions}
                  testID="device-bulk-profile"
                />
              </>
            ) : null}

            {action === 'gateMode' ? (
              <Segmented
                value={gateMode}
                onChange={setGateMode}
                testID="device-bulk-gate-mode"
                options={[
                  {value: 'entry', label: t('home.device.gate.entry')},
                  {value: 'exit', label: t('home.device.gate.exit')},
                  {value: 'both', label: t('home.device.gate.both')},
                ]}
              />
            ) : null}

            {action === 'command' ? (
              <>
                <AppText variant="caption">{t('deviceBulk.commandHint')}</AppText>
                <ChoiceChips
                  value={command}
                  onChange={next => setCommand(next as BulkCommandKind)}
                  testID="device-bulk-command"
                  options={COMMANDS.map(kind => ({
                    value: kind,
                    label: t(`commands.kind.${kind}`, kind),
                  }))}
                />
              </>
            ) : null}

            {action === 'remove' ? (
              <>
                <Banner
                  tone="warning"
                  title={t('deviceBulk.removeWarning')}
                  message={t('deviceBulk.removeScope')}
                />
                <AppButton
                  title={
                    confirmRemove
                      ? t('deviceBulk.removeArmed')
                      : t('deviceBulk.removeArm')
                  }
                  tone={confirmRemove ? 'destructive' : 'neutral'}
                  size="sm"
                  onPress={() => setConfirmRemove(v => !v)}
                  testID="device-bulk-remove-arm"
                />
              </>
            ) : null}

            {apply.isError ? (
              <Banner tone="error" title={errorOf(apply.error)} />
            ) : null}

            {outcome ? (
              <Banner
                tone={outcome.failed.length > 0 ? 'warning' : 'success'}
                title={t('bulk.outcome', {
                  ok: outcome.succeeded.length,
                  failed: outcome.failed.length,
                })}
                message={
                  outcome.failed.length > 0
                    ? outcome.failed
                        .slice(0, 3)
                        .map(f => `${f.id}: ${f.error}`)
                        .join('\n')
                    : undefined
                }
              />
            ) : null}

            <AppButton
              title={t('bulk.apply', {count: selected.length})}
              tone={action === 'remove' ? 'destructive' : 'soft'}
              disabled={!canApply}
              loading={apply.isPending}
              onPress={() => apply.mutate()}
              testID="device-bulk-apply"
            />
          </Card>
        </View>
      </View>
    </Sheet>
  );
}

/**
 * Remove several terminals the way one is removed: `factory-reset/finalize` each.
 *
 * There is a batch route for this and it is the wrong one — it clears the rows and
 * leaves the licence seats bound to hardware nobody can see any more. Finalize is a
 * single-device call, so this is N round trips; that is the price of the seats coming
 * back, and forty of them still beat forty visits to forty screens.
 *
 * Sequential, not parallel, for the same reason `runBulk` is: forty concurrent
 * finalizes is how a tidy-up becomes an outage. The result is shaped like a
 * `BulkOutcome` so the caller reports it exactly as it reports every other batch —
 * per item, with the failures named.
 */
async function removeEach(
  companyId: string,
  deviceIds: string[],
): Promise<BulkOutcome> {
  const succeeded: string[] = [];
  const failed: BulkOutcome['failed'] = [];

  for (const deviceId of deviceIds) {
    try {
      await finalizeDeviceReset(companyId, deviceId);
      succeeded.push(deviceId);
    } catch (error) {
      failed.push({
        id: deviceId,
        code: error instanceof ApiError ? error.status : 0,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return {total: deviceIds.length, succeeded, failed};
}
